import { useCallback, useEffect, useState, useRef } from "react";
import { usePostApi } from "src/hooks";
import { type IFile } from "src/interfaces";

interface FileWithData extends IFile {
  fetchedData?: string;
  isFetching?: boolean;
  fetchError?: boolean;
}

export const useGalleryFiles = (files: IFile[] = []) => {
  const [filesWithData, setFilesWithData] = useState<FileWithData[]>(files);
  const { post } = usePostApi();
  const fetchedAidsRef = useRef<Set<string | number>>(new Set());

  const fetchFileData = useCallback(
    async (file: IFile) => {
      if (file.b64 || !file.aid) {
        return file;
      }

      try {
        const response = await post({
          request: "view-file",
          body: { params: { aid: file.aid } },
        });

        return {
          ...file,
          fetchedData: response.data,
          type: response.type || file.type,
          isFetching: false,
        };
      } catch (error) {
        console.error(`Error fetching file ${file.aid}:`, error);
        setFilesWithData(prev =>
          prev.map(f =>
            f.aid === file.aid
              ? { ...f, fetchError: true, isFetching: false }
              : f,
          ),
        );
        return file;
      }
    },
    [post],
  );

  useEffect(() => {
    setFilesWithData(prev => {
      return files.map(file => {
        const existing = prev.find(f => f.aid === file.aid);
        return existing ? { ...file, ...existing, aid: file.aid } : file;
      });
    });
  }, [files]);

  useEffect(() => {
    const filesToFetch = filesWithData.filter(
      f =>
        !f.deleted &&
        !f.b64 &&
        f.aid &&
        !f.fetchedData &&
        !f.isFetching &&
        !fetchedAidsRef.current.has(f.aid),
    );

    if (filesToFetch.length === 0) return;

    const fetchFiles = async () => {
      for (const file of filesToFetch) {
        fetchedAidsRef.current.add(file.aid);
        const updatedFile = await fetchFileData(file);
        setFilesWithData(prev =>
          prev.map(f => (f.aid === file.aid ? updatedFile : f)),
        );
      }
    };

    void fetchFiles();
  }, [filesWithData, fetchFileData]);

  return filesWithData;
};
